/**
 * Utilities for building and filtering the "Changed Column" dropdown options
 * in Workflow / Sequence / Document-Wizard condition modals.
 *
 * Each option has shape:
 *   { code, name, type, group, optionKey, token_type_id? }
 * `code` / `name` are the token string (e.g. "Status").
 * `group` is the human-readable section header.
 * `optionKey` uniquely identifies the option across groups (e.g. "document::Status")
 * so Autocomplete can distinguish same-named fields under different modules.
 *
 * MUI Autocomplete `groupBy` requires all items of the same group to be contiguous,
 * so we concat groups in a deterministic order and never intermix them.
 */

export const GROUP_LABELS = {
  document: "Document",
  company: "Company",
  contact: "Contact",
  organization: "Organization Rep",
  opportunity: "Opportunity",
  task: "Task"
};

/** Reverse lookup: display group label → module key */
export const GROUP_TO_MODULE_KEY = Object.fromEntries(
  Object.entries(GROUP_LABELS).map(([key, label]) => [label, key])
);

/**
 * Which field groups appear in the Changed Column dropdown for each workflow trigger.
 * Order is preserved in the rendered list.
 */
export const WORKFLOW_MODULE_GROUP_KEYS = {
  company: ["company", "organization"],
  contact: ["company", "contact", "organization"],
  task: ["company", "contact", "organization", "document", "task", "opportunity"],
  opportunity: ["company", "opportunity", "contact", "document", "organization"],
  document: ["document", "organization", "opportunity"]
};

const OPTION_KEY_SEP = "::";

/**
 * Builds a unique option key for a module + token pair.
 * Example: makeColumnOptionKey("document", "Status") → "document::Status"
 */
export function makeColumnOptionKey(moduleKey, code) {
  if (!moduleKey || code === undefined || code === null || code === "") return code || "";
  return `${moduleKey}${OPTION_KEY_SEP}${code}`;
}

/**
 * Extracts the token code from a form value that may be either a plain token
 * ("Status") or an optionKey ("document::Status").
 */
export function getColumnToken(value) {
  if (!value || typeof value !== "string") return value || "";
  const sepIndex = value.indexOf(OPTION_KEY_SEP);
  if (sepIndex === -1) return value;
  const moduleKey = value.slice(0, sepIndex);
  if (GROUP_LABELS[moduleKey]) {
    return value.slice(sepIndex + OPTION_KEY_SEP.length);
  }
  return value;
}

/**
 * Extracts the module key from an optionKey, or undefined for a plain token.
 */
export function getColumnModuleKey(value) {
  if (!value || typeof value !== "string") return undefined;
  const sepIndex = value.indexOf(OPTION_KEY_SEP);
  if (sepIndex === -1) return undefined;
  const moduleKey = value.slice(0, sepIndex);
  return GROUP_LABELS[moduleKey] ? moduleKey : undefined;
}

/**
 * Finds an option by optionKey first, then by code.
 * When matching by code alone and multiple hits exist, prefers `preferredModuleKey`.
 */
export function findColumnOption(options, value, preferredModuleKey) {
  if (!value || !Array.isArray(options)) return undefined;

  const byKey = options.find((o) => o.optionKey === value);
  if (byKey) return byKey;

  const token = getColumnToken(value);
  const matches = options.filter((o) => o.code === token);
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];

  if (preferredModuleKey) {
    const preferred = matches.find(
      (o) =>
        GROUP_TO_MODULE_KEY[o.group] === preferredModuleKey ||
        o.optionKey?.startsWith(`${preferredModuleKey}${OPTION_KEY_SEP}`)
    );
    if (preferred) return preferred;
  }

  return matches[0];
}

/**
 * Returns a human-readable group label for a module key.
 * Falls back to the capitalized module key for unknown keys.
 */
function groupLabel(moduleKey) {
  return GROUP_LABELS[moduleKey] || `${moduleKey.charAt(0).toUpperCase()}${moduleKey.slice(1)}`;
}

/**
 * Tags a raw options array with `group` and unique `optionKey`.
 * Returns an empty array when `items` is falsy or empty.
 */
function tagWithGroup(items, moduleKey) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const label = groupLabel(moduleKey);
  return items.map((item) => ({
    ...item,
    group: label,
    optionKey: makeColumnOptionKey(moduleKey, item.code)
  }));
}

/**
 * Builds the flat, grouped options array for the "Changed Column" Autocomplete.
 *
 * Uses `WORKFLOW_MODULE_GROUP_KEYS` to determine which groups are included for
 * each trigger. Groups with no API data are omitted. Unknown triggers fall back
 * to that trigger's own fields only.
 *
 * `data` is the Redux `workflowCondition.data` object, which may be an empty
 * array `[]` on first render (before the API response arrives).
 *
 * @param {object|array} data - grouped condition tokens from the API
 * @param {string} moduleValue - workflow trigger key (e.g. "company", "task")
 * @param {object} [groupKeysMap=WORKFLOW_MODULE_GROUP_KEYS] - optional override map
 */
export function buildChangedColumnOptions(data, moduleValue, groupKeysMap = WORKFLOW_MODULE_GROUP_KEYS) {
  if (!data || Array.isArray(data) || !moduleValue) return [];

  const groupKeys = groupKeysMap[moduleValue] ?? [moduleValue];

  return groupKeys.flatMap((key) => tagWithGroup(data[key], key));
}

/**
 * Builds Column To Change options for Field Change actions.
 * Same grouped structure as Changed Column, but only includes tokens
 * where `token_type_id === 1` (editable field tokens).
 */
export function buildColumnToChangeOptions(data, moduleValue, groupKeysMap = WORKFLOW_MODULE_GROUP_KEYS) {
  return buildChangedColumnOptions(data, moduleValue, groupKeysMap).filter(
    (option) => option.token_type_id === 1
  );
}

/**
 * Filters the options array by a search query using case-insensitive substring
 * matching on `name`. Returns all options when query is empty/whitespace.
 *
 * Used as the Autocomplete `filterOptions` prop so the behaviour is isolated
 * and independently unit-testable.
 */
export function filterChangedColumnOptions(options, query) {
  if (!query || !query.trim()) return options;
  const lower = query.trim().toLowerCase();
  return options.filter((option) => option.name.toLowerCase().includes(lower));
}

/**
 * Resolves the special-field attribute config (Status select, Executed Yes/No,
 * etc.) for a selected column in Conditions or Field Change actions.
 *
 * Prefers the module matching the option's `group` (so Document → Status uses
 * document statuses even when the workflow trigger is task/opportunity), then
 * the trigger module, then any module that defines the column.
 */
export function resolveModuleFieldAttribute(
  modulesAttributes,
  columnName,
  appModuleValue,
  selectedOption
) {
  const token = getColumnToken(columnName);
  if (!token || !modulesAttributes) return undefined;

  if (selectedOption?.group) {
    const moduleKey = GROUP_TO_MODULE_KEY[selectedOption.group];
    if (moduleKey && modulesAttributes[moduleKey]?.[token]) {
      return modulesAttributes[moduleKey][token];
    }
  }

  const moduleFromKey = getColumnModuleKey(columnName);
  if (moduleFromKey && modulesAttributes[moduleFromKey]?.[token]) {
    return modulesAttributes[moduleFromKey][token];
  }

  if (modulesAttributes[appModuleValue]?.[token]) {
    return modulesAttributes[appModuleValue][token];
  }

  for (const moduleKey of Object.keys(modulesAttributes)) {
    if (modulesAttributes[moduleKey]?.[token]) {
      return modulesAttributes[moduleKey][token];
    }
  }

  return undefined;
}

/** @deprecated Prefer resolveModuleFieldAttribute — alias kept for existing call sites */
export const resolveConditionAttribute = resolveModuleFieldAttribute;

/**
 * Tags opportunity stages with a `group` (board name) and sorts them so stages
 * from the same board are contiguous — required for MUI Select / Autocomplete
 * group headers to render correctly.
 *
 * Stages whose `board_id` does not match any board fall under "Other".
 *
 * @param {Array<{ code: *, name: string, board_id?: string, position?: number }>} stages
 * @param {Array<{ code: string, name: string }>} boards
 * @returns {Array}
 */
export function buildStagesGroupedByBoard(stages, boards = []) {
  if (!Array.isArray(stages) || stages.length === 0) return [];

  const boardNameById = new Map((boards || []).map((board) => [board.code, board.name]));
  const boardOrder = new Map((boards || []).map((board, index) => [board.code, index]));

  return stages
    .map((stage) => ({
      ...stage,
      group: boardNameById.get(stage.board_id) || "Other"
    }))
    .sort((a, b) => {
      const orderA = boardOrder.has(a.board_id) ? boardOrder.get(a.board_id) : Number.MAX_SAFE_INTEGER;
      const orderB = boardOrder.has(b.board_id) ? boardOrder.get(b.board_id) : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      if (a.position != null && b.position != null) return a.position - b.position;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
}
